import { Button, Loader } from "@mantine/core";
import { IconFileTypePdf } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { prvApi } from "#/api/prvApi";
import { EmptyState } from "#/components/ui/EmptyState";
import { useClient } from "#/hook/useClient";
import PrvPatternsPanel, {
  type PrvPatternsPanelHandle,
} from "#/pages/ClientDetail/components/prv/PrvPatternsPanel";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { logger } from "#/utils/logger";

interface Props {
  subClientId: number;
}

/**
 * Read-only "Product Rotation Patterns" tab on a sub-client, for the client's
 * own users: the PRV engine's observed patterns pinned to this sub-client.
 * No engine controls here — those live on the TMA-admin Client Detail page.
 */
const SubClientPatternsTab = ({ subClientId }: Props) => {
  const t = usePrvTranslation();
  const { client } = useClient();
  const panelRef = useRef<PrvPatternsPanelHandle>(null);
  const [exporting, setExporting] = useState(false);

  const {
    data: view,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["prv-sub-client-patterns", subClientId],
    queryFn: async () => (await prvApi.getSubClientPatterns(subClientId)).data,
  });

  const handleExportPdf = async () => {
    if (!panelRef.current || exporting) return;
    setExporting(true);
    try {
      await panelRef.current.exportToPdf(
        `product-rotation-patterns-sub-client-${subClientId}-${new Date().toISOString().split("T")[0]}.pdf`
      );
    } catch (error) {
      logger.error("SubClientPatternsTab exportPdf error:", error);
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader color="var(--client-color)" />
      </div>
    );
  }
  if (isError || !view) {
    return <EmptyState title={t("common.failedToProcess")} />;
  }

  const lastRun = view.lastRun;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm text-gray-600">{t("subClients.patterns.description")}</span>
          <span className="text-xs text-gray-500">
            {lastRun?.finishedAt
              ? t("subClients.patterns.dataAsOf", {
                  date: formatDateTime(lastRun.finishedAt, client),
                })
              : t("subClients.patterns.noRunYet")}
          </span>
        </div>
        <Button
          variant="outline"
          leftSection={<IconFileTypePdf size={18} />}
          onClick={() => void handleExportPdf()}
          loading={exporting}
          disabled={view.patterns.length === 0}
        >
          {exporting ? t("common.exporting") : t("common.exportPdf")}
        </Button>
      </div>

      <PrvPatternsPanel
        ref={panelRef}
        view={view}
        lockedSubClientId={subClientId}
        showRunSummary={false}
        emptyTitle={t("subClients.patterns.empty")}
        emptyDescription={t("subClients.patterns.emptyHint")}
      />
    </div>
  );
};

export default SubClientPatternsTab;
